import React, {useContext} from 'react';
import {Typography, Card, Modal, Tooltip, Flex, Button} from 'antd';
import {DeleteOutlined} from '@ant-design/icons';
import {useItemKrTextData} from "../../../features/probability/useItemData";
import {AuthContext} from "../../contexts/authContext";
import IsLoading from "../IsLoading";
import ErrorPage from "../ErrorPage";
import {useMergeBlockImageMap, resolveMergeBlockImageUrl} from "../../hooks/useMergeBlockImageMap";
import toast from "react-hot-toast";
import {internalApi} from "../../../common/axios";
import {USER_STATE} from "../../utils/enum";

const {Text} = Typography;

const MergeGridItem = React.memo(({
                                      item,
                                      mergeMainData,
                                      playerData,
                                      trayList,
                                      platterData,
                                      eventPlatterData,
                                      setPlayerData
                                  }) => {
    const {currentUser} = useContext(AuthContext);
    const {data: krItemTextData, isLoading, error} = useItemKrTextData();
    const {data: imageMap} = useMergeBlockImageMap();
    const [isModalVisible, setIsModalVisible] = React.useState(false);

    if (isLoading) return <IsLoading/>;
    if (error) return <ErrorPage error={error}/>;

    const block = item?.MTargetBlock || item?.eventBlock;
    const isEmpty = !block;
    const blockIndex = block?.blockIndex;
    const type = item?.mItemType;
    const productCount = block?.productCount;
    const stackCount = block?.stackCount; // stackCount도 함께 표시
    const isLockedOrBubbled = block?.IsBlockState_AnyBox_AnyBubble_Lock || block?.IsBlockState_AnyBox;

    let blockDetails = {};
    if (type === 10) {
        blockDetails = mergeMainData[`${blockIndex}_10`];
    } else {
        blockDetails = mergeMainData[blockIndex];
    }
    const imageUrl = resolveMergeBlockImageUrl(blockDetails?.resourcePath, imageMap);
    const blockName = krItemTextData[currentUser.target].find(e => e.index === blockDetails?.blockNameLIdx)?.name || `Block ${blockIndex}`;
    const blockDescription = blockDetails?.description || 'No description available.';


    const showModal = () => {
        if (!isEmpty) {
            setIsModalVisible(true);
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const isEventBlock = !!item?.eventBlock;
    const canReclaim = !isEmpty && typeof setPlayerData === 'function' && !!playerData?.userKey;

    const handleReclaim = () => {
        if (playerData?.userState !== USER_STATE.SUSPEND) {
            toast.error('정지 상태인 계정만 블럭을 회수할 수 있습니다.');
            return;
        }

        Modal.confirm({
            title: '블럭 회수',
            content: `'${blockName}' 블럭을 보드에서 회수하시겠습니까? 이 작업은 되돌릴 수 없습니다.`,
            okText: '회수',
            okType: 'danger',
            cancelText: '취소',
            onOk: async () => {
                try {
                    const endpoint = isEventBlock ? '/player/mergeEvent/reclaim' : '/player/merge/reclaim';
                    const res = await internalApi.put(endpoint, {
                        userKey: playerData.userKey,
                        slotIndex: block.slotIndex,
                    });

                    setPlayerData(prev => {
                        const playerInfo = {...prev.playerInfo};
                        if (isEventBlock) {
                            const state = playerInfo.mergeEventData.ActiveMergeEventState;
                            playerInfo.mergeEventData = {
                                ...playerInfo.mergeEventData,
                                ActiveMergeEventState: {...state, boardData: {...state.boardData, slotData: res.data.slotData}},
                            };
                        } else {
                            playerInfo.mergeData = {...playerInfo.mergeData, mBlocks: res.data.mBlocks};
                        }
                        return {...prev, playerInfo, syncId: res.data.syncId};
                    });

                    toast.success('블럭이 회수되었습니다.');
                    setIsModalVisible(false);
                } catch (err) {
                    toast.error(err.response?.data?.message || '블럭 회수에 실패했습니다.');
                }
            },
        });
    };

    // 트레이(플래터) 진행도 정보
    let trayInfo = null;
    if (trayList?.has(blockIndex) && eventPlatterData) {
        const platterEntry = Object.values(eventPlatterData).find(p => p.trayIdx === blockIndex);
        if (platterEntry) {
            const curPoint = platterData?.curPoint || 0;
            const needValue = platterEntry.needValue || 0;
            const level = String(platterEntry.trayIdx).slice(-1);
            trayInfo = {curPoint, needValue, level};
        }
    }

    // 6401 주머니 블럭 보관 블럭들
    let shelfBlocks = [];
    if (blockIndex === 6401 && playerData?.playerInfo?.mergeData?.shelfBlocks) {
        shelfBlocks = playerData.playerInfo.mergeData.shelfBlocks[6401] || [];
    }

    return (
        <>
            <div
                onClick={showModal} // 클릭 이벤트 추가
                style={{
                    width: '100%', // MergeGrid에서 gap을 처리하므로 여기는 100%
                    aspectRatio: '1 / 1', // 정사각형 유지
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: isEmpty ? '#f0f0f0' : trayInfo ? '#e0d4f5' : (isLockedOrBubbled ? '#ffe0b2' : '#e6ffe6'),
                    boxSizing: 'border-box',
                    overflow: 'hidden', // 이미지가 셀을 벗어나지 않도록
                    cursor: isEmpty ? 'default' : 'pointer', // 클릭 가능한 아이템에만 커서 변경
                    padding: '2px', // 이미지 주변에 약간의 여백
                }}
            >
                {isEmpty ? (
                    <Text type="secondary" style={{color: '#aaa', fontSize: '0.8em'}}>Empty</Text>
                ) : (
                    <Tooltip title={blockName}> {/* Tooltip 추가로 hover 시 이름 표시 */}
                        {imageUrl && (
                            <img
                                src={imageUrl}
                                alt={blockName}
                                style={{
                                    width: '100%', // 셀 내부에 꽉 채우기
                                    height: '100%',
                                    objectFit: 'contain', // 이미지가 잘리지 않고 비율 유지
                                    borderRadius: '4px',
                                }}
                            />
                        )}

                        {/* 이미지 위에 텍스트 오버레이로 표시 (가독성을 위해 스타일 조정) */}
                        {productCount > 0 && (
                            <div style={{
                                position: 'absolute',
                                bottom: '2px',
                                right: '2px',
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                color: 'white',
                                fontSize: '0.7em',
                                padding: '2px 4px',
                                borderRadius: '4px',
                            }}>
                                {productCount}
                            </div>
                        )}
                        {stackCount > 1 && ( // stackCount가 1보다 클 때만 표시
                            <div style={{
                                position: 'absolute',
                                top: '2px',
                                left: '2px',
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                color: 'white',
                                fontSize: '0.7em',
                                padding: '2px 4px',
                                borderRadius: '4px',
                            }}>
                                x{stackCount}
                            </div>
                        )}
                        {/*{isLockedOrBubbled && (*/}
                        {/*    <div style={{*/}
                        {/*        position: 'fixed',*/}
                        {/*        top: '50%',*/}
                        {/*        left: '50%',*/}
                        {/*        transform: 'translate(-50%, -50%)',*/}
                        {/*        backgroundColor: 'rgba(255,0,0,0.6)',*/}
                        {/*        color: 'white',*/}
                        {/*        fontSize: '0.8em',*/}
                        {/*        padding: '4px 8px',*/}
                        {/*        borderRadius: '8px',*/}
                        {/*        whiteSpace: 'nowrap',*/}
                        {/*    }}>*/}
                        {/*        Locked!*/}
                        {/*    </div>*/}
                        {/*)}*/}
                    </Tooltip>
                )}
            </div>

            {/* 블럭 정보 모달 */}
            {!isEmpty && (
                <Modal
                    title={<Text strong style={{fontSize: '1.2em'}}>{blockName} (ID: {blockIndex})</Text>}
                    open={isModalVisible}
                    onCancel={handleCancel}
                    footer={canReclaim ? (
                        <Button danger icon={<DeleteOutlined/>} onClick={handleReclaim}>
                            블럭 회수
                        </Button>
                    ) : null}
                    centered
                    style={{padding: '20px'}}
                >
                    <Flex justify="center" align="center" style={{marginBottom: '15px'}}>
                        {imageUrl && (
                            <img
                                src={imageUrl}
                                alt={blockName}
                                style={{
                                    width: '80px',
                                    height: '80px',
                                    objectFit: 'contain',
                                    borderRadius: '8px',
                                    border: '1px solid #eee'
                                }}
                            />
                        )}
                    </Flex>
                    <Card size="small" style={{marginBottom: '10px'}}>
                        <p style={{margin: 0}}><Text strong>Slot Index:</Text> {block?.slotIndex}</p>
                        <p style={{margin: 0}}><Text strong>Block Index:</Text> {block?.blockIndex}</p>
                        {stackCount > 0 && <p style={{margin: 0}}><Text strong>Stack Count:</Text> {stackCount}</p>}
                        {productCount > 0 &&
                            <p style={{margin: 0}}><Text strong>Product Count:</Text> {productCount}</p>}
                        {block?.expireTime > 0 && <p style={{margin: 0}}><Text strong>Expire
                            Time:</Text> {new Date(block?.expireTime * 1000).toLocaleString()}</p>}
                        {block?.productStartTime > 0 && <p style={{margin: 0}}><Text strong>Product Start
                            Time:</Text> {new Date(block?.productStartTime * 1000).toLocaleString()}</p>}
                    </Card>
                    <Card size="small" title="상세 정보">
                        {!trayInfo && <p style={{margin: 0}}>{blockDescription}</p>}
                        {isLockedOrBubbled && <p style={{margin: 0, color: 'red'}}><Text strong>상태:</Text> 잠금/버블 상태</p>}
                        {trayInfo && (
                            <>
                                <p style={{margin: 0}}><Text strong>레벨:</Text> {trayInfo.level}</p>
                                <p style={{margin: 0}}><Text
                                    strong>진행도:</Text> {trayInfo.curPoint} / {trayInfo.needValue}</p>
                            </>
                        )}
                    </Card>
                    {/* 6401 주머니 블럭일 때 보관 블럭 리스트 */}
                    {blockIndex === 6401 && (
                        <Card size="small" title="보관 중인 블럭들" style={{marginTop: 12}}>
                            {shelfBlocks.length === 0 ? (
                                <Text type="secondary">보관 중인 블럭 없음</Text>
                            ) : (
                                <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                                    {shelfBlocks.map((b, idx) => {
                                        // 보관된 블럭의 이미지 경로 추출
                                        let shelfBlockDetails = {};
                                        if (mergeMainData[`${b.blockIndex}_10`]) {
                                            shelfBlockDetails = mergeMainData[`${b.blockIndex}_10`];
                                        } else {
                                            shelfBlockDetails = mergeMainData[b.blockIndex];
                                        }
                                        const shelfImageUrl = resolveMergeBlockImageUrl(shelfBlockDetails?.resourcePath, imageMap);
                                        const shelfBlockName = shelfBlockDetails?.blockName || `Block ${b.blockIndex}`;
                                        return (
                                            <div key={idx} style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 12,
                                                border: '1px solid #eee',
                                                borderRadius: 6,
                                                padding: 6
                                            }}>
                                                {shelfImageUrl && (
                                                    <img
                                                        src={shelfImageUrl}
                                                        alt={shelfBlockName}
                                                        style={{
                                                            width: 32,
                                                            height: 32,
                                                            objectFit: 'contain',
                                                            borderRadius: 4,
                                                            border: '1px solid #ddd'
                                                        }}
                                                    />
                                                )}
                                                <span><Text strong>BlockIndex:</Text> {b.blockIndex}</span>
                                                <span><Text strong>Stack:</Text> {b.stackCount}</span>
                                                <span><Text strong>Product:</Text> {b.productCount}</span>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </Card>
                    )}
                </Modal>
            )}
        </>
    );
});

export default MergeGridItem;